/* HEllO cart drawer — behaviour. India store, cart redesign, 26 Sep 2026.

   The drawer's markup is rendered entirely by Liquid (snippets/cart-drawer.liquid),
   including the strap + fitted-buckle grouping. This file only:

     1. changes quantities and removes lines BY KEY through /cart/update.js,
        always moving a strap and its buckle together, then swaps in the fresh
        'cart-drawer' section that comes back with the same response;
     2. quick-adds from the "Complete your setup" tabs (spares, straps, kit);
     3. lazily loads those tabs from sections/hello-cart-upsells and runs their
        filters, and the small bits of UI (price details, copy code, zoom,
        toast + undo).

   It never touches the product page's own ADD TO CART — wc-cart.js still owns
   that, and its responses already re-render this drawer through Dawn's
   renderContents(). wc-cart-sync.js keeps watching every /cart call, so any
   pair that somehow drifts is still repaired at the data layer.

   Every failure path leaves the cart as it was and says so in the drawer. */

(function () {
  'use strict';

  if (window.__helloCart) return;
  window.__helloCart = true;

  var SOURCE = 'hello-cart';
  var SECTIONS = 'cart-drawer,cart-icon-bubble';

  var upsellHTML = '';
  var upsellFor = '';        /* cart signature the cached tabs were rendered for */
  var upsellLoading = null;
  var ui = { tab: 'spares', lug: null, spw: null, col: '' };
  var lastRemoved = null;
  var toastTimer = null;
  var breakupOpen = false;

  /* ---------- plumbing ---------- */

  function routes() { return window.routes || {}; }
  function root() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }
  function drawer() { return document.querySelector('cart-drawer'); }
  function inner() { return document.querySelector('#CartDrawer .hc-inner'); }

  function post(url, payload) {
    return fetch(url, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(payload)
    }).then(function (r) {
      return r.json().then(function (j) {
        if (!r.ok || j.status) {
          var err = new Error(j.description || j.message || 'Something went wrong.');
          err.payload = j;
          throw err;
        }
        return j;
      });
    });
  }

  function busy(on) {
    var el = inner();
    if (el) el.classList.toggle('is-busy', !!on);
  }

  function signature(cart) {
    if (!cart || !cart.items) return '';
    return cart.items.map(function (i) { return i.key + ':' + i.quantity; }).join('|');
  }

  /* Line properties as a plain object. Older drawer markup serialised them as
     [[name, value], …]; posted back to /cart/change.js that became properties
     named "0", "1"… and split the strap from its buckle. Both shapes in,
     one shape out. */
  function normProps(p) {
    var out = {};
    if (!p) return out;
    if (Array.isArray(p)) {
      p.forEach(function (e) { if (Array.isArray(e) && e.length === 2) out[e[0]] = e[1]; });
      return out;
    }
    Object.keys(p).forEach(function (k) {
      var v = p[k];
      if (/^\d+$/.test(k) && Array.isArray(v) && v.length === 2) out[v[0]] = v[1];
      else out[k] = v;
    });
    return out;
  }
  function brokenProps(p) {
    return !!p && Object.keys(p).some(function (k) { return /^\d+$/.test(k) && Array.isArray(p[k]); });
  }

  /* Repairs lines already damaged by that bug (a cart built before the fix):
     rewrites their properties once, so the strap finds its buckle again. */
  function healCart() {
    return fetch(root() + 'cart.js', { credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var bad = (cart.items || []).filter(function (i) { return brokenProps(i.properties); });
        if (!bad.length) return;
        return bad.reduce(function (chain, i) {
          return chain.then(function () {
            return post((routes().cart_change_url || '/cart/change') + '.js', {
              id: i.key, quantity: i.quantity, properties: normProps(i.properties)
            });
          });
        }, Promise.resolve()).then(refresh);
      })
      .catch(function () { /* nothing to heal, or offline */ });
  }

  /* ---------- render ---------- */

  /* Swap in a freshly rendered drawer, keeping the shopper where they were:
     same scroll position, same open "Price details", and the upsell tabs put
     straight back from cache so they don't flash while they reload. */
  function renderSections(sections) {
    if (!sections) return;
    var html = sections['cart-drawer'];
    var target = document.getElementById('CartDrawer');
    if (html && target) {
      var doc = new DOMParser().parseFromString(html, 'text/html');
      var src = doc.getElementById('CartDrawer');
      if (src) {
        var body = target.querySelector('[data-hc-body]');
        var y = body ? body.scrollTop : 0;
        target.innerHTML = src.innerHTML;
        var nb = target.querySelector('[data-hc-body]');
        if (nb) nb.scrollTop = y;
        var empty = !target.querySelector('[data-hc-line]');
        var cd = drawer();
        if (cd) cd.classList.toggle('is-empty', empty);
        restoreBreakup();
        mountUpsells(false);
      }
    }
    var bubble = sections['cart-icon-bubble'];
    var bubbleEl = document.getElementById('cart-icon-bubble');
    if (bubble && bubbleEl) {
      var b = new DOMParser().parseFromString(bubble, 'text/html').querySelector('.shopify-section');
      if (b) bubbleEl.innerHTML = b.innerHTML;
    }
  }

  function refresh() {
    return fetch((routes().cart_url || '/cart') + '?sections=' + SECTIONS, { credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(renderSections)
      .catch(function () { /* leave what's on screen */ });
  }

  function publish(cart) {
    try {
      if (typeof window.publish === 'function' && window.PUB_SUB_EVENTS) {
        window.publish(window.PUB_SUB_EVENTS.cartUpdate, { source: SOURCE, cartData: cart });
      }
    } catch (e) { /* not load-bearing */ }
  }

  /* ---------- quantity / remove ---------- */

  function update(updates) {
    busy(true);
    return post((routes().cart_update_url || '/cart/update') + '.js', {
      updates: updates,
      sections: SECTIONS,
      sections_url: window.location.pathname
    }).then(function (cart) {
      renderSections(cart.sections);
      publish(cart);
      return cart;
    }).catch(function (e) {
      toast(e.message || 'Could not update your cart.');
    }).then(function (cart) {
      busy(false);
      return cart;
    });
  }

  function setQty(btn) {
    var q = parseInt(btn.getAttribute('data-hc-qty'), 10);
    if (isNaN(q) || q < 0) q = 0;
    if (q === 0) return removeLine(btn);
    var u = {};
    u[btn.getAttribute('data-hc-key')] = q;
    var mate = btn.getAttribute('data-hc-mate');
    if (mate) u[mate] = q;        /* the fitted buckle always matches its strap */
    update(u);
  }

  function removeLine(btn) {
    var card = btn.closest('[data-hc-line]');
    var lines = [];
    try { lines = JSON.parse(card.getAttribute('data-hc-lines') || '[]'); } catch (e) { lines = []; }
    var title = btn.getAttribute('data-hc-title') ||
      ((card.querySelector('.hc-name') || {}).textContent || 'Item').trim();

    var u = {};
    u[btn.getAttribute('data-hc-key')] = 0;
    var mate = btn.getAttribute('data-hc-mate');
    if (mate) u[mate] = 0;

    update(u).then(function (cart) {
      if (!cart) return;
      lastRemoved = lines;
      toast('Removed <b>' + escapeHTML(title) + '</b>' + (mate ? ' and its fitted buckle' : ''), true);
    });
  }

  function undo() {
    if (!lastRemoved || !lastRemoved.length) return;
    var items = lastRemoved.map(function (l) {
      return { id: l.id, quantity: l.quantity, properties: normProps(l.properties) };
    });
    lastRemoved = null;
    hideToast();
    addItems(items, null);
  }

  /* ---------- adding ---------- */

  function addItems(items, btn) {
    if (btn) { btn.classList.add('is-busy'); btn.textContent = 'Adding'; }
    busy(true);
    return post((routes().cart_add_url || '/cart/add') + '.js', {
      items: items,
      sections: SECTIONS,
      sections_url: window.location.pathname
    }).then(function (res) {
      renderSections(res.sections);
      publish(null);
      return true;
    }).catch(function (e) {
      toast(e.message || 'Could not add that to your cart.');
      if (btn) { btn.classList.remove('is-busy'); btn.textContent = '+ Add'; }
      return false;
    }).then(function (ok) {
      busy(false);
      return ok;
    });
  }

  function groupId() {
    return 'wc-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 7);
  }

  function quickAdd(btn) {
    var kind = btn.getAttribute('data-hc-add');

    if (kind === 'spare') {
      addItems([{
        id: Number(btn.getAttribute('data-vid')),
        quantity: 1,
        properties: { _wc_role: 'spare', Fits: btn.getAttribute('data-fits') }
      }], btn).then(function (ok) { if (ok) toast('Spare added. It ships loose, not fitted.'); });
      return;
    }

    if (kind === 'kit') {
      addItems([{ id: Number(btn.getAttribute('data-vid')), quantity: 1 }], btn)
        .then(function (ok) { if (ok) toast('Added to your cart.'); });
      return;
    }

    if (kind === 'strap') {
      var card = btn.closest('[data-hc-strap]');
      var pick = strapPick(card, ui.lug);
      if (!pick) return;
      if (pick.b && pick.bOk === false) {
        toast('Its default buckle is out of stock in this width. Open the strap to choose another.');
        return;
      }
      var items;
      if (pick.b) {
        /* Same shape as the product page sends (wc-cart.js): two lines, one
           _wc_group, the buckle described on the strap for the packing slip. */
        var g = groupId();
        var sp = { _wc_group: g, _wc_role: 'strap' };
        if (pick.bl) sp.Buckle = pick.bl;
        if (pick.t) sp._wc_taper = String(pick.t);
        items = [
          { id: pick.v, quantity: 1, properties: sp },
          { id: pick.b, quantity: 1, properties: { _wc_group: g, _wc_role: 'buckle' } }
        ];
      } else {
        items = [{ id: pick.v, quantity: 1 }];
      }
      addItems(items, btn).then(function (ok) {
        if (ok) toast('Added with its default ' + (/clasp/i.test(pick.bl || '') ? 'clasp' : 'buckle') + '. Change it anytime in the cart.');
      });
    }
  }

  /* ---------- upsell tabs ---------- */

  function cartSignature() {
    var lines = document.querySelectorAll('#CartDrawer [data-hc-line] [data-hc-key]');
    var keys = [];
    lines.forEach(function (el) { keys.push(el.getAttribute('data-hc-key')); });
    return keys.join('|');
  }

  function mountUpsells(force) {
    var host = document.querySelector('#CartDrawer [data-hc-upsells]');
    if (!host) return;
    var sig = cartSignature();

    if (upsellHTML) {
      host.innerHTML = upsellHTML;
      host.classList.remove('is-loading');
      applyUpsellUI(host);
    }
    if (!force && upsellHTML && sig === upsellFor) return;
    if (upsellLoading) return;

    upsellLoading = fetch((routes().cart_url || '/cart') + '?section_id=hello-cart-upsells', { credentials: 'same-origin' })
      .then(function (r) { return r.ok ? r.text() : ''; })
      .then(function (html) {
        if (!html) return;
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var u = doc.querySelector('[data-hc-u]');
        if (!u) return;
        upsellHTML = u.outerHTML;
        upsellFor = sig;
        var h = document.querySelector('#CartDrawer [data-hc-upsells]');
        if (h) {
          h.innerHTML = upsellHTML;
          h.classList.remove('is-loading');
          applyUpsellUI(h);
        }
      })
      .catch(function () {
        var h = document.querySelector('#CartDrawer [data-hc-upsells]');
        if (h && !upsellHTML) h.closest('.hc-ups-wrap').hidden = true;
      })
      .then(function () { upsellLoading = null; });
  }

  /* The strap tile's pick for this lug and the chosen colour chip: the
     colourway itself (variant, name, photo, price) plus the lug's fitted
     buckle. With no colour chosen, the first in-stock colourway. */
  function strapPick(card, lug, col) {
    var map = {};
    try { map = JSON.parse(card.getAttribute('data-lugs') || '{}'); } catch (e) { map = {}; }
    var e = map[String(lug)];
    if (!e) return null;
    var vs = e.vs || [];
    if (col === undefined) col = ui.col;
    var hit = col
      ? vs.filter(function (x) { return (' ' + (x.f || '') + ' ').indexOf(' ' + col + ' ') > -1; })[0]
      : vs[0];
    if (!hit) return null;
    return { v: hit.v, c: hit.c, p: hit.p, img: hit.img, b: e.b, bOk: e.bOk, bl: e.bl, t: e.t };
  }

  function applyUpsellUI(host) {
    var u = host.querySelector('[data-hc-u]');
    if (!u) return;
    if (ui.lug == null) ui.lug = parseInt(u.getAttribute('data-default-lug'), 10) || 20;
    if (ui.spw == null) ui.spw = parseInt(u.getAttribute('data-default-spare'), 10) || 18;

    /* An empty cart has no strap to match spares to, so it opens on straps;
       once the shopper picks a tab, that choice sticks. */
    if (!ui.tabPicked) {
      var cd = host.closest('cart-drawer');
      ui.tab = cd && cd.classList.contains('is-empty') ? 'straps' : 'spares';
    }
    /* sold-out spares go to the end of the rail, once per render */
    var spRail = u.querySelector('[data-hc-panel="spares"] [data-hc-rail]');
    if (spRail && !spRail.hasAttribute('data-hc-sorted')) {
      spRail.setAttribute('data-hc-sorted', '');
      Array.prototype.slice.call(spRail.querySelectorAll('[data-hc-spare]')).forEach(function (c) {
        if (c.querySelector('.hc-add.is-off')) spRail.appendChild(c);
      });
    }

    /* tabs */
    u.querySelectorAll('[data-hc-tab]').forEach(function (b) {
      b.setAttribute('aria-selected', String(b.getAttribute('data-hc-tab') === ui.tab));
    });
    u.querySelectorAll('[data-hc-panel]').forEach(function (p) {
      p.hidden = p.getAttribute('data-hc-panel') !== ui.tab;
    });

    /* spares: by width */
    var spareCards = u.querySelectorAll('[data-hc-spare]');
    var spareWidths = {};
    spareCards.forEach(function (c) { spareWidths[c.getAttribute('data-w')] = true; });
    if (!spareWidths[String(ui.spw)]) {
      var firstW = Object.keys(spareWidths).sort(function (a, b) { return a - b; })[0];
      if (firstW) ui.spw = parseInt(firstW, 10);
    }
    var spareShown = 0;
    spareCards.forEach(function (c) {
      var on = c.getAttribute('data-w') === String(ui.spw);
      c.hidden = !on;
      if (on) spareShown++;
    });
    u.querySelectorAll('[data-hc-spw]').forEach(function (b) {
      b.setAttribute('aria-pressed', String(b.getAttribute('data-hc-spw') === String(ui.spw)));
    });
    var sparePanel = u.querySelector('[data-hc-panel="spares"]');
    if (sparePanel) sparePanel.querySelector('[data-hc-empty]').hidden = spareShown > 0;

    /* straps: by lug, then colour; card text follows the chosen lug */
    var strapCards = u.querySelectorAll('[data-hc-strap]');
    var lugsSeen = {};
    var strapShown = 0;
    strapCards.forEach(function (c) {
      var map = {};
      try { map = JSON.parse(c.getAttribute('data-lugs') || '{}'); } catch (e) { map = {}; }
      Object.keys(map).forEach(function (k) { lugsSeen[k] = true; });
      var pick = strapPick(c, ui.lug, ui.col);
      var on = !!pick && strapShown < 16;
      c.hidden = !on;
      if (!on) return;
      strapShown++;
      var meta = c.querySelector('[data-hc-meta]');
      var price = c.querySelector('[data-hc-price]');
      var img = c.querySelector('[data-hc-img]');
      if (meta) meta.textContent = (pick.c ? pick.c + ' · ' : '') + ui.lug + 'mm' + (pick.bl ? ' · with ' + pick.bl.split(' · ')[0] : '');
      if (price) price.textContent = pick.p;
      if (img && pick.img && img.getAttribute('src') !== pick.img) img.setAttribute('src', pick.img);
    });
    u.querySelectorAll('[data-hc-lug]').forEach(function (b) {
      var l = b.getAttribute('data-hc-lug');
      b.hidden = !lugsSeen[l];
      b.setAttribute('aria-pressed', String(l === String(ui.lug)));
    });
    u.querySelectorAll('[data-hc-col]').forEach(function (b) {
      b.setAttribute('aria-pressed', String(b.getAttribute('data-hc-col') === ui.col));
    });
    var label = u.querySelector('[data-hc-lug-label]');
    if (label) label.textContent = ui.lug + 'mm';
    var strapPanel = u.querySelector('[data-hc-panel="straps"]');
    if (strapPanel) strapPanel.querySelector('[data-hc-empty]').hidden = strapShown > 0;
  }

  function reapply() {
    var host = document.querySelector('#CartDrawer [data-hc-upsells]');
    if (host) applyUpsellUI(host);
  }

  /* ---------- change the fitted buckle, in the cart ---------- */

  /* The Change link on a strap's fitted-buckle row. Options come from the same
     place the product page's drawer reads them: the strap's
     custom.buckle_options families (handles carried on the button), filtered to
     the width that fits this strap's buckle end (its taper). Family data is
     fetched from /products/<handle>.js on first open and cached.

     The swap keeps the pair intact at every step, so wc-cart-sync.js never sees
     a strap without a buckle and never "restores" the old one:
       1. add the new buckle line into the same _wc_group,
       2. remove the old buckle line,
       3. rewrite the strap's "Buckle" line property so the order and the
          packing slip name the new part. */

  var famCache = {};

  function getFamily(handle) {
    if (!famCache[handle]) {
      famCache[handle] = fetch(root() + 'products/' + handle + '.js', { credentials: 'same-origin' })
        .then(function (r) { return r.ok ? r.json() : null; })
        .catch(function () { return null; });
    }
    return famCache[handle];
  }

  function toInt(t) { var n = parseInt(String(t || '').replace(/[^\d]/g, ''), 10); return isNaN(n) ? 0 : n; }

  function rupees(cents) {
    return '₹' + Math.round(cents / 100).toLocaleString('en-IN');
  }

  /* Which photo shows a finish + colour. The variant's own image first; then
     the store's alt convention "<Colour> <Finish>__…" (same as the straps);
     then the same colour in another finish. Never the product cover — that is
     one colourway (brushed silver), which is how every buckle ended up
     looking silver. No photo → an empty thumbnail. */
  function altKey(m) {
    var a = String((m && m.alt) || '');
    return a.indexOf('__') > -1 ? a.split('__')[0].trim().toLowerCase() : '';
  }
  function shotFor(p, finish, colour, v) {
    var media = (p.media || []).filter(function (m) { return m && m.src && (m.media_type === 'image' || !m.media_type); });
    var key = (String(colour || '') + ' ' + String(finish || '')).trim().toLowerCase();
    var ck = String(colour || '').trim().toLowerCase() + ' ';
    var own = v && v.featured_image && (v.featured_image.src || v.featured_image);
    var hit = media.filter(function (m) { return altKey(m) === key; })[0] ||
      media.filter(function (m) { var k = altKey(m); return k && (k + ' ').indexOf(ck) === 0; })[0];
    var src = (typeof own === 'string' && own) || (hit && hit.src) || '';
    if (!src) return '';
    if (src.indexOf('//') === 0) src = 'https:' + src;
    return src + (src.indexOf('?') > -1 ? '&' : '?') + 'width=160';
  }

  var NOIMG = '<span class="hc-noimg" aria-hidden="true"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"><rect x="5" y="4.5" width="14" height="15" rx="3.2"/><path d="M5 12h14"/><path d="M12 12V3"/></svg></span>';

  function kindOf(title) {
    return /clasp|butterfly|deployant|deployment|folding/i.test(title || '') ? 'clasp' : 'buckle';
  }

  function pickerEl() {
    var host = document.querySelector('#CartDrawer .hc-inner');
    if (!host) return null;
    var el = host.querySelector('[data-hc-picker]');
    if (!el) {
      el = document.createElement('div');
      el.className = 'hc-picker';
      el.setAttribute('data-hc-picker', '');
      el.setAttribute('role', 'dialog');
      el.setAttribute('aria-modal', 'true');
      el.hidden = true;
      el.innerHTML =
        '<div class="hc-picker__sheet">' +
          '<div class="hc-picker__head"><div><b data-hc-picker-title>Change buckle</b><span data-hc-picker-sub></span></div>' +
          '<button type="button" class="hc-picker__close" data-hc-picker-close aria-label="Close">×</button></div>' +
          '<div class="hc-picker__list" data-hc-picker-list></div>' +
        '</div>';
      host.appendChild(el);
    }
    return el;
  }

  var pickCtx = null;

  function openPicker(btn) {
    var el = pickerEl();
    if (!el) return;
    var props = {};
    try { props = normProps(JSON.parse(btn.getAttribute('data-props') || '{}')); } catch (e) { props = {}; }
    pickCtx = {
      fams: (btn.getAttribute('data-fams') || '').split(',').filter(Boolean),
      taper: parseInt(btn.getAttribute('data-taper'), 10) || 0,
      def: btn.getAttribute('data-default') || '',
      current: parseInt(btn.getAttribute('data-current'), 10) || 0,
      strapKey: btn.getAttribute('data-strap-key'),
      buckleKey: btn.getAttribute('data-buckle-key'),
      qty: parseInt(btn.getAttribute('data-qty'), 10) || 1,
      group: btn.getAttribute('data-group') || '',
      props: props,
      options: []
    };
    el.querySelector('[data-hc-picker-title]').textContent = 'Change buckle or clasp';
    el.querySelector('[data-hc-picker-sub]').textContent = 'Everything that fits a ' + pickCtx.taper + 'mm buckle end';
    var list = el.querySelector('[data-hc-picker-list]');
    list.innerHTML = '<p class="hc-picker__msg">Loading options…</p>';
    el.hidden = false;

    var ctx = pickCtx;
    Promise.all(ctx.fams.map(getFamily)).then(function (products) {
      if (ctx !== pickCtx) return;
      var opts = [];
      products.forEach(function (p, i) {
        if (!p) return;
        (p.variants || []).forEach(function (v) {
          if (toInt(v.option3) !== ctx.taper) return;
          opts.push({
            id: v.id,
            handle: ctx.fams[i],
            title: p.title,
            kind: kindOf(p.title),
            finish: v.option1 || '',
            colour: v.option2 || '',
            price: v.price,
            available: v.available,
            img: shotFor(p, v.option1, v.option2, v)
          });
        });
      });
      /* Buckles before clasps (they sit under two headings); the strap's
         default buckle first, then the product's buckle_options order; within
         a type silver → gold → black → rose gold, brushed before polished —
         the same silver-first order the product page uses for the default. */
      var COL = ['silver', 'gold', 'black', 'rose gold'];
      var cr = function (c) { var i = COL.indexOf(String(c || '').trim().toLowerCase()); return i < 0 ? COL.length : i; };
      var fr = function (f) { return /brush/i.test(f || '') ? 0 : 1; };
      opts.sort(function (a, b) {
        if (a.kind !== b.kind) return a.kind === 'buckle' ? -1 : 1;
        var da = a.handle === ctx.def ? 0 : 1, db = b.handle === ctx.def ? 0 : 1;
        if (da !== db) return da - db;
        if (a.handle !== b.handle) return ctx.fams.indexOf(a.handle) - ctx.fams.indexOf(b.handle);
        return (cr(a.colour) - cr(b.colour)) || (fr(a.finish) - fr(b.finish)) || (a.price - b.price);
      });
      ctx.options = opts;
      if (!opts.length) {
        list.innerHTML = '<p class="hc-picker__msg">No other buckles fit this strap right now.</p>';
        return;
      }
      var html = '';
      var lastKind = '';
      opts.forEach(function (o, i) {
        if (o.kind !== lastKind) {
          html += '<p class="hc-picker__group">' + (o.kind === 'clasp' ? 'Clasps' : 'Buckles') + '</p>';
          lastKind = o.kind;
        }
        var cur = o.id === ctx.current;
        var priceTxt = cur ? 'Fitted' : (o.price === 0 ? 'Included' : '+' + rupees(o.price));
        var priceCls = cur ? ' is-cur' : (o.price === 0 ? ' is-incl' : '');
        html +=
          '<button type="button" class="hc-opt' + (cur ? ' is-current' : '') + '" data-hc-pick="' + i + '"' +
            (!o.available && !cur ? ' disabled' : '') + '>' +
            '<span class="hc-opt__img">' + (o.img ? '<img src="' + escapeHTML(o.img) + '" alt="" loading="lazy" width="52" height="52">' : NOIMG) + '</span>' +
            '<span><span class="hc-opt__name">' + escapeHTML(o.title) + '</span>' +
            '<span class="hc-opt__meta">' + escapeHTML(o.colour + ' · ' + o.finish) + '</span>' +
            (o.handle === ctx.def && o.price === 0 ? '<span class="hc-opt__tag">Standard</span>' : '') +
            (!o.available ? '<span class="hc-opt__meta">Sold out</span>' : '') + '</span>' +
            '<span class="hc-opt__price' + priceCls + '">' + priceTxt + '</span>' +
          '</button>';
      });
      list.innerHTML = html;
    });
  }

  function closePicker() {
    var el = document.querySelector('#CartDrawer [data-hc-picker]');
    if (el) el.hidden = true;
    pickCtx = null;
  }

  function applyPick(i) {
    var ctx = pickCtx;
    if (!ctx) return;
    var o = ctx.options[i];
    if (!o || !o.available) return;
    if (o.id === ctx.current) { closePicker(); return; }

    var label = o.title + ' · ' + o.colour + ' · ' + o.finish;
    var props = {};
    Object.keys(ctx.props || {}).forEach(function (k) { props[k] = ctx.props[k]; });
    props.Buckle = label;

    closePicker();
    busy(true);

    var url = routes();
    post((url.cart_add_url || '/cart/add') + '.js', {
      items: [{ id: o.id, quantity: ctx.qty, properties: { _wc_group: ctx.group, _wc_role: 'buckle' } }]
    }).then(function () {
      var u = {};
      u[ctx.buckleKey] = 0;
      return post((url.cart_update_url || '/cart/update') + '.js', { updates: u });
    }).then(function () {
      return post((url.cart_change_url || '/cart/change') + '.js', {
        id: ctx.strapKey,
        quantity: ctx.qty,
        properties: props,
        sections: SECTIONS,
        sections_url: window.location.pathname
      });
    }).then(function (res) {
      renderSections(res.sections);
      publish(res);
      toast('Fitted ' + (o.kind === 'clasp' ? 'clasp' : 'buckle') + ' changed to <b>' + escapeHTML(o.title) + '</b>');
    }).catch(function (e) {
      toast(e.message || 'Could not change the buckle. Please try again.');
      refresh();
    }).then(function () {
      busy(false);
    });
  }

  /* ---------- product viewer (upsell tiles) ----------

     Tapping a tile's photo opens a swipeable viewer with the photos of THAT
     colourway, the price, Add and a link to the full page — so a shopper can
     decide inside the cart. Photos come from /products/<handle>.js on first
     open (cached), matched by the alt-text key the store uses everywhere:
       spare buckle/clasp — "<Colour> <Finish>__…"; same colour in another
                             finish as the fallback; never another colour
       strap              — "<Colour>__…" for the colour shown on the tile
       WatchLife kit      — every photo of the product
     Add in the viewer presses the tile's own Add, so one code path adds. */

  var VIEW_MAX = 10;

  function viewEl() {
    var host = inner();
    if (!host) return null;
    var el = host.querySelector('[data-hc-view-box]');
    if (!el) {
      el = document.createElement('div');
      el.className = 'hc-view';
      el.setAttribute('data-hc-view-box', '');
      el.setAttribute('role', 'dialog');
      el.setAttribute('aria-modal', 'true');
      el.hidden = true;
      host.appendChild(el);
    }
    return el;
  }

  function sized(src, w) {
    if (!src) return '';
    if (src.indexOf('//') === 0) src = 'https:' + src;
    return src + (src.indexOf('?') > -1 ? '&' : '?') + 'width=' + w;
  }

  function viewPhotos(p, mode, key, col) {
    var media = (p.media || []).filter(function (m) { return m && m.src && (m.media_type === 'image' || !m.media_type); });
    var keyed = media.some(function (m) { return !!altKey(m); });
    var hit = [];
    if (mode === 'hw') {
      hit = media.filter(function (m) { return altKey(m) === key; });
      if (!hit.length && col) hit = media.filter(function (m) { var k = altKey(m); return k && (k + ' ').indexOf(col + ' ') === 0; });
    } else if (mode === 'strap' && key) {
      hit = media.filter(function (m) { return altKey(m) === key; });
      if (!hit.length && !keyed) hit = media;
    } else {
      /* kit: the shown colourway's photos when the product is alt-keyed, else all */
      hit = key ? media.filter(function (m) { return altKey(m) === key; }) : [];
      if (!hit.length) hit = media;
    }
    return hit.slice(0, VIEW_MAX).map(function (m) { return { src: m.src, alt: m.alt || '' }; });
  }

  function openViewer(btn) {
    var card = btn.closest('.hc-up');
    var el = viewEl();
    if (!card || !el) return;
    var mode = btn.getAttribute('data-mode') || 'kit';
    var handle = btn.getAttribute('data-hc-view');
    var key = (btn.getAttribute('data-key') || '').toLowerCase();
    var col = (btn.getAttribute('data-col') || '').toLowerCase();
    if (mode === 'strap') {
      var pick = strapPick(card, ui.lug);
      key = pick && pick.c ? String(pick.c).trim().toLowerCase() : '';
    }
    var txt = function (sel) { var n = card.querySelector(sel); return n ? n.textContent.trim() : ''; };
    var add = card.querySelector('.hc-add');
    var canAdd = add && !add.classList.contains('is-off') && add.tagName === 'BUTTON';
    var own = card.querySelector('.hc-up__img img');
    var fallback = own ? [{ src: own.getAttribute('src').replace(/([?&])width=\d+/, '$1width=320'), alt: '' }] : [];

    el.innerHTML =
      '<div class="hc-view__box">' +
        '<button type="button" class="hc-view__x" data-hc-view-close aria-label="Close">×</button>' +
        '<div class="hc-view__stage"><div class="hc-view__track" data-hc-view-track><div class="hc-view__slide is-loading"></div></div>' +
          '<button type="button" class="hc-view__nav hc-view__nav--prev" data-hc-view-step="-1" aria-label="Previous photo" hidden>&#8249;</button>' +
          '<button type="button" class="hc-view__nav hc-view__nav--next" data-hc-view-step="1" aria-label="Next photo" hidden>&#8250;</button>' +
        '</div>' +
        '<div class="hc-view__dots" data-hc-view-dots></div>' +
        '<div class="hc-view__info">' +
          '<p class="hc-view__title">' + escapeHTML(txt('.hc-up__title')) + '</p>' +
          '<p class="hc-view__meta">' + escapeHTML(txt('.hc-up__meta')) + '</p>' +
          '<div class="hc-view__row"><b>' + escapeHTML(txt('.hc-up__foot b')) + '</b>' +
            (canAdd ? '<button type="button" class="hc-add hc-view__add" data-hc-view-add>+ Add to cart</button>'
                    : '<span class="hc-add is-off">Sold out</span>') +
          '</div>' +
          '<a class="hc-view__link" href="' + escapeHTML(btn.getAttribute('data-url') || '#') + '">View full details</a>' +
        '</div>' +
      '</div>';
    el.hidden = false;
    viewCtx = { card: card, at: 0, n: 0 };
    var ctx = viewCtx;

    getFamily(handle).then(function (p) {
      if (ctx !== viewCtx) return;
      var shots = p ? viewPhotos(p, mode, key, col) : [];
      if (!shots.length) shots = fallback;
      paintViewer(el, shots, txt('.hc-up__title'));
    });
  }

  var viewCtx = null;

  function paintViewer(el, shots, label) {
    var track = el.querySelector('[data-hc-view-track]');
    var dots = el.querySelector('[data-hc-view-dots]');
    if (!track) return;
    viewCtx.n = shots.length;
    viewCtx.at = 0;
    track.innerHTML = shots.length
      ? shots.map(function (s, i) {
          return '<div class="hc-view__slide"><img src="' + escapeHTML(sized(s.src, 900)) + '" alt="' +
            escapeHTML(s.alt.split('__').pop() || label) + '"' + (i ? ' loading="lazy"' : '') + '></div>';
        }).join('')
      : '<div class="hc-view__slide hc-view__slide--none">' + NOIMG + '<span class="hc-view__soon">Photos coming soon</span></div>';
    var many = shots.length > 1;
    el.querySelectorAll('[data-hc-view-step]').forEach(function (b) { b.hidden = !many; });
    dots.innerHTML = many ? shots.map(function (s, i) {
      return '<button type="button" class="hc-view__dot' + (i ? '' : ' is-on') + '" data-hc-view-dot="' + i + '" aria-label="Photo ' + (i + 1) + ' of ' + shots.length + '"></button>';
    }).join('') : '';
    var settle = null;
    track.addEventListener('scroll', function () {
      clearTimeout(settle);
      settle = setTimeout(function () { markDot(el, Math.round(track.scrollLeft / (track.clientWidth || 1))); }, 60);
    }, { passive: true });
  }

  function markDot(el, i) {
    if (!viewCtx) return;
    viewCtx.at = i;
    el.querySelectorAll('[data-hc-view-dot]').forEach(function (d) {
      d.classList.toggle('is-on', parseInt(d.getAttribute('data-hc-view-dot'), 10) === i);
    });
  }

  function viewGo(i) {
    var el = document.querySelector('#CartDrawer [data-hc-view-box]');
    if (!el || !viewCtx || !viewCtx.n) return;
    i = Math.max(0, Math.min(viewCtx.n - 1, i));
    var track = el.querySelector('[data-hc-view-track]');
    var w = track.clientWidth || 1;
    if (track.scrollTo) track.scrollTo({ left: i * w, behavior: 'smooth' }); else track.scrollLeft = i * w;
    markDot(el, i);
  }

  function closeViewer() {
    var el = document.querySelector('#CartDrawer [data-hc-view-box]');
    if (el) { el.hidden = true; el.innerHTML = ''; }
    viewCtx = null;
  }

  function viewerAdd() {
    var card = viewCtx && viewCtx.card;
    closeViewer();
    var add = card && card.querySelector('.hc-add');
    if (add && !add.classList.contains('is-off')) quickAdd(add);
  }

  /* ---------- special instructions (cart note) ---------- */

  /* Saved as the shopper types (debounced) and again on blur, so leaving the
     field for checkout never loses the last few words. Shopify puts the cart
     note on the order ("Notes" in the order admin). No section re-render here:
     swapping the drawer out from under someone typing would steal the caret. */
  var noteTimer = null;
  var noteSaved = null;

  function saveNote(el, now) {
    clearTimeout(noteTimer);
    var run = function () {
      var val = el.value;
      if (val === noteSaved) return;
      var status = document.querySelector('#CartDrawer [data-hc-note-status]');
      if (status) status.textContent = 'Saving…';
      post((routes().cart_update_url || '/cart/update') + '.js', { note: val })
        .then(function () {
          noteSaved = val;
          var st = document.querySelector('#CartDrawer [data-hc-note-status]');
          if (st) st.textContent = val ? 'Saved to your order' : '';
        })
        .catch(function () {
          var st = document.querySelector('#CartDrawer [data-hc-note-status]');
          if (st) st.textContent = 'Could not save. Please try again.';
        });
    };
    if (now) run(); else noteTimer = setTimeout(run, 600);
  }

  document.addEventListener('input', function (e) {
    if (e.target.matches && e.target.matches('#CartDrawer [data-hc-note]')) saveNote(e.target, false);
  });
  document.addEventListener('focusout', function (e) {
    if (e.target.matches && e.target.matches('#CartDrawer [data-hc-note]')) saveNote(e.target, true);
  });

  /* ---------- small UI ---------- */

  function escapeHTML(s) {
    return String(s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  function toast(html, withUndo) {
    var t = document.querySelector('#CartDrawer [data-hc-toast]');
    if (!t) return;
    t.querySelector('[data-hc-toast-text]').innerHTML = html;
    t.querySelector('[data-hc-undo]').hidden = !withUndo;
    t.classList.add('is-on');
    clearTimeout(toastTimer);
    toastTimer = setTimeout(hideToast, withUndo ? 5000 : 3200);
  }
  function hideToast() {
    var t = document.querySelector('#CartDrawer [data-hc-toast]');
    if (t) t.classList.remove('is-on');
  }

  function restoreBreakup() {
    var btn = document.querySelector('#CartDrawer [data-hc-breakup]');
    var box = document.getElementById('hc-breakup');
    if (!btn || !box) return;
    box.hidden = !breakupOpen;
    btn.setAttribute('aria-expanded', String(breakupOpen));
  }

  function copyCode(btn) {
    var code = btn.getAttribute('data-hc-copy');
    var done = function () {
      btn.textContent = 'Copied';
      setTimeout(function () { btn.textContent = code; }, 1600);
    };
    try { navigator.clipboard.writeText(code).then(done, done); } catch (e) { done(); }
  }

  function openZoom(btn) {
    var z = document.querySelector('#CartDrawer [data-hc-zoom]');
    var src = btn.getAttribute('data-hc-zoom-src');
    if (!z || !src) return;
    z.querySelector('[data-hc-zoom-img]').setAttribute('src', src);
    z.querySelector('[data-hc-zoom-cap]').textContent = btn.getAttribute('data-hc-zoom-cap') || '';
    z.hidden = false;
  }
  function closeZoom() {
    var z = document.querySelector('#CartDrawer [data-hc-zoom]');
    if (z) z.hidden = true;
  }

  /* ---------- wiring (delegated: the drawer's markup is replaced often) ---------- */

  document.addEventListener('click', function (e) {
    if (!e.target.closest) return;
    if (!e.target.closest('#CartDrawer')) return;

    /* Dawn binds the overlay's close once, to an element this drawer re-renders. */
    if (e.target.id === 'CartDrawer-Overlay') {
      var cd = drawer();
      if (cd && cd.close) cd.close();
      return;
    }

    var b;
    if ((b = e.target.closest('[data-hc-change]'))) { e.preventDefault(); openPicker(b); return; }
    if ((b = e.target.closest('[data-hc-pick]'))) { e.preventDefault(); if (!b.disabled) applyPick(parseInt(b.getAttribute('data-hc-pick'), 10)); return; }
    if (e.target.closest('[data-hc-picker-close]') || e.target.matches('[data-hc-picker]')) { closePicker(); return; }
    if ((b = e.target.closest('[data-hc-qty]'))) { e.preventDefault(); setQty(b); return; }
    if ((b = e.target.closest('[data-hc-remove]'))) { e.preventDefault(); removeLine(b); return; }
    if ((b = e.target.closest('[data-hc-undo]'))) { e.preventDefault(); undo(); return; }
    if ((b = e.target.closest('[data-hc-add]'))) {
      e.preventDefault();
      if (!b.classList.contains('is-off')) quickAdd(b);
      return;
    }
    if ((b = e.target.closest('[data-hc-tab]'))) { ui.tab = b.getAttribute('data-hc-tab'); ui.tabPicked = true; reapply(); return; }
    if ((b = e.target.closest('[data-hc-spw]'))) { ui.spw = parseInt(b.getAttribute('data-hc-spw'), 10); reapply(); return; }
    if ((b = e.target.closest('[data-hc-lug]'))) { ui.lug = parseInt(b.getAttribute('data-hc-lug'), 10); reapply(); return; }
    if ((b = e.target.closest('[data-hc-col]'))) {
      /* One colour at a time; tapping the chosen one again clears it. */
      var c = b.getAttribute('data-hc-col');
      ui.col = ui.col === c ? '' : c;
      reapply();
      return;
    }
    if ((b = e.target.closest('[data-hc-breakup]'))) { breakupOpen = !breakupOpen; restoreBreakup(); return; }
    if ((b = e.target.closest('[data-hc-copy]'))) { copyCode(b); return; }
    if ((b = e.target.closest('[data-hc-view-add]'))) { e.preventDefault(); viewerAdd(); return; }
    if ((b = e.target.closest('[data-hc-view-step]'))) { viewGo((viewCtx ? viewCtx.at : 0) + parseInt(b.getAttribute('data-hc-view-step'), 10)); return; }
    if ((b = e.target.closest('[data-hc-view-dot]'))) { viewGo(parseInt(b.getAttribute('data-hc-view-dot'), 10)); return; }
    if (e.target.closest('[data-hc-view-close]') || e.target.matches('[data-hc-view-box]')) { closeViewer(); return; }
    if (e.target.closest('[data-hc-view-box]')) return;   /* taps on the photo or text stay in the viewer */
    if ((b = e.target.closest('[data-hc-view]'))) { e.preventDefault(); openViewer(b); return; }
    if ((b = e.target.closest('[data-hc-zoom-src]'))) { e.preventDefault(); openZoom(b); return; }
    if (e.target.closest('[data-hc-zoom-close]') || e.target.matches('[data-hc-zoom]')) { closeZoom(); return; }
  });

  document.addEventListener('keyup', function (e) {
    if (e.key === 'Escape') { closeZoom(); closePicker(); closeViewer(); }
    if (viewCtx && e.key === 'ArrowRight') viewGo(viewCtx.at + 1);
    if (viewCtx && e.key === 'ArrowLeft') viewGo(viewCtx.at - 1);
  });

  /* Some other part of the site changed the cart (the product page, the
     strap/buckle repair in wc-cart-sync.js, an app). Re-render from the
     server so the drawer never shows a stale pair. Dawn's own product form
     already re-renders through renderContents(), so that source is skipped. */
  var extTimer = null;
  function onExternalChange() {
    clearTimeout(extTimer);
    extTimer = setTimeout(refresh, 200);   /* wc-cart-sync fires several signals at once */
  }

  /* GoKwik's checkout button arrives disabled (spinner showing) and is only
     switched on by assets/wc-checkout-guard.js. That script is included by
     snippets/gokwik-checkout.liquid, which sits inside the checkout block the
     drawer only renders when the cart has something in it. On a page opened
     with an empty cart the guard therefore never loaded, and a button that
     appeared later spun forever. Load it here, once, whatever the cart holds.
     The guard is idempotent (window.__wcCheckoutGuard). */
  var ME = document.currentScript && document.currentScript.src;
  function ensureCheckoutGuard() {
    if (window.__wcCheckoutGuard || document.querySelector('script[data-hc-guard]')) return;
    var src = ME ? ME.replace(/hello-cart\.js(\?[^#]*)?$/, 'wc-checkout-guard.js') : '';
    if (!src || src === ME) return;
    var s = document.createElement('script');
    s.src = src;
    s.defer = true;
    s.setAttribute('data-hc-guard', '');
    document.head.appendChild(s);
  }

  function boot() {
    ensureCheckoutGuard();
    healCart();
    mountUpsells(false);

    /* The drawer is also re-rendered by Dawn's renderContents() after a
       product-page add. Watch for our markup being replaced and put the
       upsell tabs back. */
    var cd = drawer();
    if (cd && window.MutationObserver) {
      new MutationObserver(function () {
        var host = document.querySelector('#CartDrawer [data-hc-upsells].is-loading');
        if (host) mountUpsells(false);
      }).observe(cd, { childList: true, subtree: true });
    }

    try {
      if (typeof window.subscribe === 'function' && window.PUB_SUB_EVENTS) {
        window.subscribe(window.PUB_SUB_EVENTS.cartUpdate, function (evt) {
          var src = evt && evt.source;
          if (src === SOURCE || src === 'product-form') return;
          onExternalChange();
        });
      }
    } catch (e) { /* pubsub not present */ }

    ['wc:cart-synced', 'cart:refresh'].forEach(function (name) {
      document.addEventListener(name, onExternalChange);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
